/**
 * LocalStorage Cache with TTL for Streameater
 * Persistent caching that survives page reloads
 */

import { CACHE_VERSION, LOCAL_STORAGE_CONFIG } from "./cacheConfig";

interface StoredEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  version: string;
}

const STORAGE_PREFIX = "se_cache_";

/**
 * Check if localStorage is available
 */
const isStorageAvailable = (): boolean => {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get item from localStorage with TTL check
 */
export const getFromStorage = <T>(key: string): T | null => {
  if (!isStorageAvailable()) return null;

  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + key);
    if (!stored) return null;

    const entry: StoredEntry<T> = JSON.parse(stored);

    // Check version - invalidate if outdated
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }

    // Check TTL
    if (Date.now() - entry.timestamp > entry.ttl) {
      // Keep for stale-while-revalidate, but return null for fresh check
      return null;
    }

    return entry.data;
  } catch (error) {
    console.warn("[LocalStorage] Failed to get:", key, error);
    return null;
  }
};

/**
 * Get item even if stale (for stale-while-revalidate pattern)
 */
export const getStaleFromStorage = <T>(
  key: string,
): { data: T; isStale: boolean } | null => {
  if (!isStorageAvailable()) return null;

  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + key);
    if (!stored) return null;

    const entry: StoredEntry<T> = JSON.parse(stored);

    // Check version - invalidate if outdated
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }

    const isStale = Date.now() - entry.timestamp > entry.ttl;

    return {
      data: entry.data,
      isStale,
    };
  } catch (error) {
    console.warn("[LocalStorage] Failed to get stale:", key, error);
    return null;
  }
};

/**
 * Set item in localStorage with TTL
 */
export const setInStorage = <T>(key: string, data: T, ttl: number): boolean => {
  if (!isStorageAvailable()) return false;

  try {
    // Check storage quota before writing
    cleanupIfNeeded();

    const entry: StoredEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      version: CACHE_VERSION,
    };

    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    return true;
  } catch (error) {
    // Likely quota exceeded
    console.warn("[LocalStorage] Failed to set:", key, error);
    cleanup(); // Force cleanup and retry
    try {
      const entry: StoredEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl,
        version: CACHE_VERSION,
      };
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * Remove item from localStorage
 */
export const removeFromStorage = (key: string): void => {
  if (!isStorageAvailable()) return;
  localStorage.removeItem(STORAGE_PREFIX + key);
};

/**
 * Clear all cache entries
 */
export const clearStorageCache = (): void => {
  if (!isStorageAvailable()) return;

  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Clear entries matching a pattern
 */
export const clearStoragePattern = (pattern: string | RegExp): number => {
  if (!isStorageAvailable()) return 0;

  const regex = typeof pattern === "string" ? new RegExp(pattern) : pattern;
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      const cacheKey = key.replace(STORAGE_PREFIX, "");
      if (regex.test(cacheKey)) {
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  return keysToRemove.length;
};

/**
 * Get cache statistics
 */
export const getStorageStats = () => {
  if (!isStorageAvailable()) {
    return { available: false, items: 0, size: 0, expiredItems: 0 };
  }

  let items = 0;
  let expiredItems = 0;
  let totalSize = 0;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      items++;
      const value = localStorage.getItem(key) || "";
      totalSize += value.length * 2; // UTF-16

      try {
        const entry = JSON.parse(value);
        if (Date.now() - entry.timestamp > entry.ttl) {
          expiredItems++;
        }
      } catch {
        // Invalid entry
      }
    }
  }

  return {
    available: true,
    items,
    expiredItems,
    size: totalSize,
    sizeFormatted: formatBytes(totalSize),
  };
};

/**
 * Cleanup expired entries
 */
export const cleanup = (): number => {
  if (!isStorageAvailable()) return 0;

  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      try {
        const value = localStorage.getItem(key);
        if (!value) continue;

        const entry = JSON.parse(value);

        // Remove if expired or wrong version
        if (
          entry.version !== CACHE_VERSION ||
          Date.now() - entry.timestamp > entry.ttl
        ) {
          keysToRemove.push(key);
        }
      } catch {
        // Invalid entry, remove it
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  return keysToRemove.length;
};

/**
 * Check if cleanup is needed and perform if threshold exceeded
 */
const cleanupIfNeeded = (): void => {
  const stats = getStorageStats();
  if (!stats.available) return;

  // Count our cache items
  let cacheItemCount = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      cacheItemCount++;
    }
  }

  // Cleanup if over threshold
  if (
    cacheItemCount >=
    LOCAL_STORAGE_CONFIG.MAX_ITEMS * LOCAL_STORAGE_CONFIG.CLEANUP_THRESHOLD
  ) {
    cleanup();
  }
};

/**
 * Format bytes to human readable
 */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
